import { supportedOAuthTypes } from "./supportedOAuthTypes";
import { createOAuthClient } from "./createOAuthClient";
import FCLoggger from "../../fclogger";
import {
  InsecureContextException,
  OutdatedBrowserException,
} from "../../managedException";

export function OAuthAPI(openIdConfigs) {
  checkConfigStructure(openIdConfigs);

  this.openIdConfigs = openIdConfigs;
  // this.authClient = createOAuthClient(this.openIdConfigs);
  this.logger = new FCLoggger();
  this.authClients = {};

  this.getAuthClient = (clientId) => {
    if (!this.authClients[clientId]) {
      const config = findConfigByClientId(openIdConfigs, clientId);
      if (!config)
        throw new Error(
          `No configuration found for the specified clientId ${clientId}`
        );
      this.authClients[clientId] = createOAuthClient(config);
    }
    return this.authClients[clientId];
  };

  this.login = async (clientId) => {
    try {
      const tokens = await this.getAuthClient(clientId).login();
      localStorage.setItem("fcLogInOpenId", clientId);
      return tokens;
    } catch (error) {
      FCLoggger.info(
        `Error occurred while loggin with the OAuth API : ${JSON.stringify(
          error
        )}`
      );
      if (error.xhr?.responseJSON?.error) {
        throw new Error(
          `${error.xhr.responseJSON.error} : ${error.xhr.responseJSON.error_description}`
        );
      } else {
        throw error;
      }
    }
  };

  this.getAccessToken = (clientId) =>
    this.getAuthClient(clientId).getAccessToken();

  this.isLoggedIn = async (clientId) =>
    await this.getAuthClient(clientId).isLoggedIn();

  this.logout = async () => {
    const loggedInClientId = localStorage.getItem("fcLogInOpenId");
    if (loggedInClientId) {
      await this.getAuthClient(loggedInClientId).logout();
    } else {
      // attempt logout on each available authclient.
      Object.values(this.authClients).forEach(
        async (authclient) => await safeLogout(authclient)
      );
    }
  };

  this.isOIDCRedirect = (clientId) => {
    const config = findConfigByClientId(this.openIdConfigs, clientId);
    const currentURL = window.location.toString();
    const containsRedirectURI = currentURL.indexOf(config.redirectUri) === 0;
    const hashOrSearch = getHashOrSearch(config);
    return (
      containsRedirectURI &&
      !hasFCErrorInUrl(hashOrSearch) &&
      (hasOpenIdErrorInUrl(hashOrSearch) || hasAuthorizationCode(hashOrSearch))
    );
  };

  this.safeLogout = async (authClient) => {
    try {
      await authClient.logout();
    } catch (error) {
      this.logger(
        `Error trying to logout from client: ${authclient.OAuthConfig.clientId}. Error : ${error}`
      );
    }
  };
}

function findConfigByClientId(openIdConfigs, clientId) {
  return openIdConfigs.find((config) => config.clientId === clientId);
}

function getHashOrSearch(OAuthConfig) {
  // var codeFlow = isCodeFlow(options); it's always code flow
  var useQuery = OAuthConfig?.responseMode !== "fragment";
  return useQuery ? window.location.search : window.location.hash;
}

function hasOpenIdErrorInUrl(hashOrSearch) {
  return (
    /(error=)/i.test(hashOrSearch) || /(error_description)/i.test(hashOrSearch)
  );
}

function hasFCErrorInUrl(hashOrSearch) {
  return /(fcerr=)/i.test(hashOrSearch);
}

function hasAuthorizationCode(hashOrSearch) {
  return /(code=)/i.test(hashOrSearch);
}

function checkConfigStructure(openIdConfigs) {
  if (!Array.isArray(openIdConfigs)) {
    throw new Error(
      "Please check the OpenID configurations in the loginPageConfig.js. It should be an array!"
    );
  }
  const configIds = openIdConfigs.map((config) => config.clientId);
  if (configIds.length !== new Set(configIds).size) {
    throw new Error(
      "Please check the OpenID configurations in the loginPageConfig.js. A unique clientId must be specified for each openID server."
    );
  }
  openIdConfigs.forEach((config) => doValidation(config));
}

function doValidation(OAuthConfig) {
  validateOAuthConfig(OAuthConfig);
  if (!isBrowserContextSecure()) {
    throw new InsecureContextException();
  }
  if (!isPKCESupportedBytheBrowser()) {
    throw new OutdatedBrowserException();
  }
}

/**
 * Validates whether the provided OAuthConfig is valid.
 */
function validateOAuthConfig(OAuthConfig) {
  const OAuthConfigError = "OAuth Configuration error: ";
  if (!OAuthConfig) {
    throw new Error(
      `${OAuthConfigError} OAuth Configuration object is missing.`
    );
  }

  const OAuthType = OAuthConfig.OAuthType;
  if (!OAuthType || !Object.values(supportedOAuthTypes).includes(OAuthType)) {
    throw new Error(`${OAuthConfigError} Supplied OAuthType is not valid.`);
  }
  // More validation to come
  if (!OAuthConfig.issuer) {
    throw new Error(`${OAuthConfigError} Value for OAuth issuer is required.`);
  }
  if (!OAuthConfig.clientId) {
    throw new Error(
      `${OAuthConfigError} value for OAuth ClientId is required.`
    );
  }
  if (!OAuthConfig.redirectUri) {
    throw new Error(
      `${OAuthConfigError} value for OAuth redirectUri is required.`
    );
  }
  if (!OAuthConfig.signoutRedirectUri) {
    throw new Error(
      `${OAuthConfigError} value for OAuth signoutRedirectUri is required.`
    );
  }
}

function isBrowserContextSecure() {
  return window.isSecureContext || window.location.protocol === "https:";
}

function isPKCESupportedBytheBrowser() {
  // According to the documentation on Okta JS, browsers need Crypto.sublte
  // and TextEncoder functionality
  const isCryptoSupported =
    typeof crypto !== "undefined" &&
    crypto !== null &&
    typeof crypto.subtle !== "undefined" &&
    typeof Uint8Array !== "undefined";

  const hasTextEncoder = typeof TextEncoder !== "undefined";
  return isCryptoSupported && hasTextEncoder;
}
